<template>
  <div>
    <div class="mt-4 px-4 w-full flex align-items-start" style="min-height: 300px">
      <div
        v-if="!evidenciaStore.isLoading && evidenciaStore.evidenciasList.length === 0"
        class="w-full flex flex-column gap-2 justify-content-center align-items-center"
      >
        <p class="secondaryFont empty-evidence-text">
          {{
            manageEvidence
              ? "No hay Evidencias en esta actuación. Cree una nueva evidencia"
              : "No hay Evidencias en esta actuación."
          }}
        </p>
        <Button
          v-if="manageEvidence"
          label="Nueva Evidencia"
          class="button newEvidenceButton"
          @click="handleAddEvidencia"
        >
          <div class="w-full flex justify-content-center align-items-center gap-2">
            <Plus :size="20" color="white" />
            Nueva Evidencia
          </div>
        </Button>
      </div>
      <div
        v-else
        class="evidencia-card mt-4 px-4 w-full flex justify-content-between align-items-center"
      >
        <h2 class="primaryFont title">Evidencia</h2>
        <Button label="Ver" class="button secondaryFont">
          <a :href="evidenciaStore.evidenciasList[0].Url" target="_blank" rel="noopener noreferrer">
            <div class="w-full flex justify-content-center align-items-center gap-2">
              Ver
              <ArrowRight :size="20" color="white" />
            </div>
          </a>
        </Button>
      </div>
    </div>

    <CustomModal
      ref="evidenciasModalRef"
      :title="modalConfig.title"
      :message="modalConfig.message"
      :modalType="modalConfig.type"
      :requireAction="
        modalConfig.mode !== 'delete' &&
        modalConfig.mode !== 'success' &&
        modalConfig.mode !== 'confirmEdit'
      "
      :loading="modalConfig.loading"
      :showSecondaryButton="modalConfig.showSecondaryButton"
      :button-labels="[modalConfig.confirmLabel, modalConfig.cancelLabel]"
      @confirm="handleModalConfirm"
      @cancel="closeModalTemplate(evidenciasModalRef)"
    >
      <template #action="{ setValid }">
        <div class="flex flex-column gap-4">
          <div class="flex flex-column">
            <InputText
              v-if="modalConfig.hasInput"
              type="text"
              placeholder="Ingrese la url de la evidencia."
              v-model="urlEvidence"
              @input="setValid(!showError && urlEvidence?.trim().length > 0)"
            />
            <label v-if="showError" class="text-red-500">{{ urlError }}</label>
          </div>
        </div>
      </template>
    </CustomModal>
  </div>
</template>
<script>
import CustomButton from "@/components/custom-button/CustomButton.vue";
import { useOfflineCompat } from "@/composables/useOfflineCompat";
import { useStoreCompat } from "@/composables/useStoreCompat";
import { useEvidenciaStore } from "@/store/Pinia/useEvidenciaStore";
import { defineComponent, ref, computed, watch } from "vue";
import { ArrowRight, Plus } from "lucide-vue";

export default defineComponent({
  name: "EvidenciasTab",
  components: {
    CustomButton,
    ArrowRight,
    Plus,
  },
  setup() {
    const store = useStoreCompat();
    const evidenciaStore = useEvidenciaStore();
    const offline = useOfflineCompat();

    const sumario = computed(() => store.state.sumario);
    const user = computed(() => store.state.user);

    const urlEvidence = ref("");
    const ActuacionEvidenciaID = ref("");

    const evidenciasModalRef = ref(null);
    const urlError = ref("");

    const modalConfig = ref({
      title: "",
      message: "",
      hasInput: false,
      cancelLabel: "Cancelar",
      confirmLabel: "Confirmar",
      mode: "",
      type: "info",
      showSecondaryButton: true,
      loading: false,
    });

    const isOnlineActuacion = () => {
      return offline.onlineMode && sumario.value.id?.search("offline-") != 0;
    };

    const manageEvidence = computed(() => {
      const tipoUsuario = user.value?.userdata.tipoUsuario;
      const usuariosPermitidos = ["Dpto.Criminalistica", "Lab. Informatico Forense", "Super User"];
      return (
        (isOnlineActuacion() || !offline.onlineMode) &&
        sumario.value?.sumarioEstado !== "Finalizada" &&
        usuariosPermitidos.includes(tipoUsuario)
      );
    });

    const openModalTemplate = (type) => {
      type.value?.open();
    };

    const closeModalTemplate = (type) => {
      if (type.value) {
        type.value?.close();
      } else {
        type.close();
      }
      if (type === evidenciasModalRef || type.value === evidenciasModalRef.value) {
        evidenciaStore.closeEditModal();
        evidenciaStore.closeDeleteModal();
        evidenciaStore.closeCreateModal();
        if (modalConfig.value.mode === "success") {
          urlEvidence.value = "";
        }
      }
    };

    const isValidURL = (url) => {
      try {
        new URL(url);
        return true;
      } catch (_) {
        return false;
      }
    };

    const setModalAttributes = ({
      title,
      message,
      hasInput,
      cancelLabel = "Cancelar",
      confirmLabel = "Confirmar",
      mode = "add",
      type = null,
      showSecondaryButton = true,
      loading = false,
    }) => {
      modalConfig.value = {
        title,
        message,
        hasInput,
        cancelLabel,
        confirmLabel,
        mode,
        type: type || (mode === "delete" ? "delete" : "warning"),
        showSecondaryButton,
        loading,
      };
    };

    const handleAddEvidencia = () => {
      if (!manageEvidence.value) return;
      urlEvidence.value = "";
      setModalAttributes({
        title: "Ingresar evidencia",
        message:
          "Por favor, ingrese la URL de la evidencia correspondiente en el campo a continuación.",
        hasInput: true,
        confirmLabel: "Guardar",
      });
      urlError.value = "";
      openModalTemplate(evidenciasModalRef);
    };

    watch(urlEvidence, (val) => {
      if (val.trim() === "" || !isValidURL(val)) {
        urlError.value = "La URL ingresada no es válida.";
      } else {
        urlError.value = "";
      }
    });

    watch(
      () => evidenciaStore.shouldOpenEditModal,
      (shouldOpen) => {
        if (shouldOpen) {
          const url = evidenciaStore.currentEvidenciaUrl;
          const evidencia = evidenciaStore.evidenciasList[0];
          if (url && evidencia) {
            handleEditEvidencia(url, evidencia.ActuacionEvidenciaID);
          }
          evidenciaStore.closeEditModal();
        }
      }
    );

    watch(
      () => evidenciaStore.shouldOpenDeleteModal,
      (shouldOpen) => {
        if (shouldOpen) {
          const evidencia = evidenciaStore.evidenciasList[0];
          if (evidencia) {
            handleDeleteEvidencia(evidencia.ActuacionEvidenciaID);
          }
          evidenciaStore.closeDeleteModal();
        }
      }
    );

    watch(
      () => evidenciaStore.shouldOpenCreateModal,
      (shouldOpen) => {
        if (shouldOpen) {
          handleAddEvidencia();
          evidenciaStore.closeCreateModal();
        }
      }
    );

    watch(
      () => evidenciaStore.isSubmitting,
      (isSubmitting) => {
        modalConfig.value.loading = isSubmitting;
      }
    );

    const handleEditEvidencia = (url, urlID) => {
      if (!manageEvidence.value) return;
      setModalAttributes({
        title: "Editar evidencia",
        message:
          "Modifique la URL de la evidencia en el campo a continuación y confirme los cambios.",
        hasInput: true,
        mode: "edit",
      });
      ActuacionEvidenciaID.value = urlID;
      urlEvidence.value = url;
      openModalTemplate(evidenciasModalRef);
    };

    const handleDeleteEvidencia = (urlID) => {
      if (!manageEvidence.value) return;
      setModalAttributes({
        title: "Eliminar Evidencia",
        message: "¿Está seguro de eliminar esta evidencia? Esta acción no se puede deshacer.",
        hasInput: false,
        confirmLabel: "Eliminar",
        cancelLabel: "Cancelar",
        mode: "delete",
      });
      ActuacionEvidenciaID.value = urlID;
      openModalTemplate(evidenciasModalRef);
    };

    const confirmEditEvidence = () => {
      setModalAttributes({
        title: "¿Está seguro de que desea editar la URL de la evidencia?",
        message: "Si es así, haga clic en Confirmar. De lo contrario, haga clic en Cancelar.",
        hasInput: false,
        mode: "confirmEdit",
      });
      openModalTemplate(evidenciasModalRef);
    };

    const editEvidence = async () => {
      const success = await evidenciaStore.editEvidence(
        ActuacionEvidenciaID.value,
        urlEvidence.value
      );
      if (success) {
        showSuccessModal();
      }
    };

    const showSuccessModal = () => {
      setModalAttributes({
        title: "¡Los datos se guardaron con éxito!",
        message: "",
        hasInput: false,
        confirmLabel: "Aceptar",
        showSecondaryButton: false,
        mode: "success",
        type: "success",
      });
      openModalTemplate(evidenciasModalRef);
    };

    const addEvidence = async () => {
      const success = await evidenciaStore.addEvidence(urlEvidence.value);
      if (success) {
        showSuccessModal();
      }
    };

    const deleteEvidence = async () => {
      await evidenciaStore.deleteEvidence(ActuacionEvidenciaID.value);
      closeModalTemplate(evidenciasModalRef);
    };

    const showError = computed(() => {
      const isAddOrEdit = modalConfig.value.mode === "add" || modalConfig.value.mode === "edit";
      return isAddOrEdit && (urlError.value || !urlEvidence.value.trim());
    });

    const handleModalConfirm = async () => {
      switch (modalConfig.value.mode) {
        case "add":
          await addEvidence();
          break;
        case "edit":
          confirmEditEvidence();
          break;
        case "delete":
          await deleteEvidence();
          break;
        case "confirmEdit":
          await editEvidence();
          break;
        case "success":
          closeModalTemplate(evidenciasModalRef);
          urlEvidence.value = "";
          ActuacionEvidenciaID.value = "";
          break;
      }
    };

    return {
      // Store
      evidenciaStore,

      // UI State
      evidenciasModalRef,
      modalConfig,
      urlEvidence,
      urlError,

      // Computed
      manageEvidence,
      showError,

      // Methods
      handleAddEvidencia,
      handleEditEvidencia,
      handleDeleteEvidencia,
      openModalTemplate,
      closeModalTemplate,
      isValidURL,
      handleModalConfirm,
    };
  },
});
</script>
<style scoped>
.evidencia-card {
  height: 103px;
  background: #eeeeee33;
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px #00000040;
}

.title {
  color: black;
  font-weight: 700;
  font-size: 24px;
  font-style: bold;
  margin: 0;
}

.input-order {
  min-width: 250px;
  margin-top: -17px;
}

.button {
  min-width: 105px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  border: none;
  border-radius: 6px;
  color: white;
  background-color: #0b5195;
  font-weight: 500;
  line-height: 24px;
}

.button a {
  text-decoration: none;
  color: white;
}

.button:hover {
  background-color: #09447c;
}

.empty-evidence-text {
  font-size: 24px;
  font-weight: 700;
  font-style: bold;
  color: #0b5195;
}

.newEvidenceButton {
  min-width: 450px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}
</style>
